import React from 'react'
import styles from './UIGamePlayerInfoExtraFooter.module.scss'
import clsx from 'clsx'
import {useMerchantIcon} from './useMerchantIcon'
import {PlayerColorWithoutNone} from '../../../utils/gameColors'
import IconSpecialBuildPhaseFlag from '@static/images/icon_special_build_phase_flag.svg'
// eslint-disable-next-line no-restricted-imports
import {robberSkinController} from '../../../../../../code/client/shared/scripts/ui_game/UIGameRobberSkinController'
import {T, Tooltip} from '@colonist/ui'
import {TranslationObject} from '@colonist/i18n'
import {UIGameDice} from '../../../common/UIGameDiceGroup/UIGameDice/UIGameDice'
import {AnimatedWaitIcon} from '../../../common/AnimatedWaitIcon'
import {Timer} from '../../../common/Timer/Timer'
import {useTimerLowTime} from '../../../hooks/useTimerLowTime'
import {useGameStore} from '../../../store/GameStoreContext'
import {ModeSettingEnum} from '@colonist/types'

export interface UIGamePlayerInfoExtraFooterDice {
    dice1: number
    dice2: number
    eventDice?: number
}

export interface UIGamePlayerInfoExtraFooterProps {
    isSpecialBuildPhaseRequested: boolean
    isMerchant: boolean
    isRushNextRobber: boolean
    isWaiting?: boolean
    playerColor: PlayerColorWithoutNone
    playerUsername: string
    dice?: UIGamePlayerInfoExtraFooterDice
    outOfTurnActionText?: TranslationObject
    timeLeftInSeconds?: number
}

export const UIGamePlayerInfoExtraFooter = ({
    isSpecialBuildPhaseRequested,
    isMerchant,
    isRushNextRobber,
    isWaiting,
    playerColor,
    playerUsername,
    dice,
    outOfTurnActionText,
    timeLeftInSeconds,
}: UIGamePlayerInfoExtraFooterProps) => {
    const merchantIcon = useMerchantIcon(playerColor)
    const robberIcon = robberSkinController.getRobberSkin()
    const isColonistRush = useGameStore((state) => state.gameSettings.modeSetting === ModeSettingEnum.ColonistRush4P)
    const isLowTime = useTimerLowTime(timeLeftInSeconds ?? 0, timeLeftInSeconds !== undefined)

    const shouldRenderIcons = isSpecialBuildPhaseRequested || isMerchant || isRushNextRobber
    const shouldRenderContainer = shouldRenderIcons || !!outOfTurnActionText || isWaiting
    const shouldRenderDice = dice !== undefined

    const isEmpty = !shouldRenderContainer && !shouldRenderDice
    if(isEmpty) return null

    return (
        <div className={styles.wrapper}>
            {shouldRenderContainer && <div className={clsx(styles.container, {
                [styles.rush]: isColonistRush,
            })}>
                {outOfTurnActionText
                    ? <span className={styles.outOfTurnActionText}>
                        <T id={outOfTurnActionText} />
                        {isWaiting && <AnimatedWaitIcon className={styles.waitIcon} animationType="fade" color="white" size="default" />}
                    </span>
                    : isWaiting && <AnimatedWaitIcon className={styles.waitIcon} animationType="fade" color="white" size="small" />}
                {timeLeftInSeconds !== undefined && <Timer timeLeftInSeconds={timeLeftInSeconds}
                    className={clsx(styles.time, {[styles.lowTime]: isLowTime})} />}
                {shouldRenderIcons && <div className={styles.icons}>
                    {isSpecialBuildPhaseRequested && <img src={IconSpecialBuildPhaseFlag} />}
                    {isMerchant && <img src={merchantIcon} />}
                    {isRushNextRobber && <Tooltip
                        title={<T id='strings:game.tips.rushNextRobber.title' />}
                        content={<T id='strings:game.tips.rushNextRobber.body.0' options={{playerUsername: playerUsername}} />}>
                        <img src={robberIcon} />
                    </Tooltip>}
                </div>}
            </div>}
            {shouldRenderDice && <div className={styles.diceGroup}>
                {dice.eventDice !== undefined && <UIGameDice
                    value={dice.eventDice}
                    type='event'
                    active={false}
                    className={styles.eventDie}
                />}
                <UIGameDice
                    value={dice.dice1}
                    type={dice.eventDice !== undefined ? 'standardRed' : 'standardWhite'}
                    active={false}
                    className={styles.die}
                />
                <UIGameDice
                    value={dice.dice2}
                    type='standardWhite'
                    active={false}
                    className={styles.die}
                />
            </div>}
        </div>
    )
}
